import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { runInChunks } from '@lib/fetchAllPages';
import { services } from '@config/services';
import type {
  DataRetentionPolicy,
  Merchant,
  MerchantConfigEnvelope,
  MerchantConfiguration,
  MerchantInput,
  MerchantListResponse,
  MerchantReason,
  MerchantSchema,
  MerchantSearchArgs,
  MerchantSearchResponse,
  ReasonInput,
} from './types';

export interface InviteMerchantsArgs {
  /** Legacy `merchantTypes`: ['Parent'] | ['Single'] | ['Child']. */
  types: string[];
  query?: string;
  /** Parent scope — sent only for child-merchant (Subaccount) so the API
   *  returns that parent's subaccounts; null/omitted for admin associations. */
  merchantId?: string | null;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

/**
 * Build the Invite Step-2 merchant-picker URL — verbatim port of the legacy
 * `merchantServices.getMerchants` query string (param order + the
 * `sortDesc = (sortDir==='asc')` quirk + the leading `&`). Exported so the wire
 * shape can be tested directly. Live-confirmed on portal.dev.
 */
export function buildInviteMerchantsUrl({
  types,
  query,
  merchantId,
  page = 1,
  pageSize = 50,
  sortBy = 'name',
  sortDir = 'asc',
}: InviteMerchantsArgs): string {
  let qs = '';
  const add = (k: string, v: string | number | boolean | null | undefined): void => {
    if (v !== null && v !== undefined && v !== '') qs += `&${k}=${encodeURIComponent(String(v))}`;
  };
  add('query', query);
  add('page', page);
  add('pageSize', pageSize);
  add('sortBy', sortBy);
  add('sortDesc', sortDir === 'asc');
  for (const t of types) qs += `&types=${encodeURIComponent(t)}`;
  add('merchantId', merchantId);
  return `${services.merchant}/v1/merchant?${qs}`;
}

/**
 * Unwrap the `GET /merchant/v1/merchant/bulk` response. Live envelope
 * (verified 2026-06-01 on portal.dev + local :3001):
 *   { bulk: [{ status, response: { id, name, isParent, children, ... } }] }
 * — the SAME per-row envelope as the vendor bulk, NOT a bare array or
 * `{ merchants }`. The original transform assumed the latter and silently
 * returned [] (empty org chips / account names). Unwrap status-200 rows; still
 * tolerate a bare array / `{ merchants }` defensively. Exported so the
 * envelope contract can be tested directly.
 */
export type MerchantsBulkEnvelope =
  | Merchant[]
  | { merchants?: Merchant[]; bulk?: Array<{ status?: number; response?: Merchant }> };

export function unwrapMerchantsBulk(response: MerchantsBulkEnvelope): Merchant[] {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.bulk)) {
    return response.bulk.filter((row) => row.status === 200 && row.response).map((row) => row.response!);
  }
  return response.merchants ?? [];
}

/**
 * Hard cap of merchant-service's bulk GET (`MerchantController.BulkGet`): a
 * request with more ids is rejected whole with a 400, not truncated.
 */
export const MERCHANT_BULK_MAX_IDS = 50;
/** Batches in flight at once; the service walks each batch's ids sequentially. */
const MERCHANT_BULK_CONCURRENCY = 4;

/** Read/unknown split for a page of merchant configs, keyed by merchant id. */
export interface BulkConfigResult {
  /** Configs read successfully. A missing id was NOT read — never treat it as empty. */
  configs: Record<string, MerchantConfiguration>;
  /** Ids whose row came back non-200: no access, or a failed read. */
  failures: string[];
}

/**
 * Unwrap `GET /config/v1/config/bulk` against the ids that were requested.
 *
 * Zipped positionally, because an error row carries no owner id of its own. A
 * 200 row's `ownerId` is preferred where present, so a server-side reordering
 * cannot quietly attribute one merchant's settings to another.
 *
 * Anything that is not a 200 row lands in `failures` instead of being folded in
 * as an empty config: for a settings list, "could not read" and "set to false"
 * must not render the same way. Exported so the envelope contract is lockable
 * in a unit test.
 */
export function unwrapBulkConfigs(
  response: { bulk?: Array<{ status?: number; response?: MerchantConfigEnvelope }> },
  requestedIds: readonly string[],
): BulkConfigResult {
  const configs: Record<string, MerchantConfiguration> = {};
  const rows = response?.bulk ?? [];

  requestedIds.forEach((requestedId, index) => {
    const row = rows[index];
    if (!row || row.status !== 200 || !row.response) return;
    configs[row.response.ownerId || requestedId] = row.response.data;
  });

  // Derived in one pass rather than accumulated, so configs and failures are
  // mutually exclusive by construction: a requested id failed exactly when no
  // config arrived for it. Appending during the loop cannot express that — a
  // reordered payload (a 403 first, then a 200 whose ownerId is the id the 403
  // sat against) would put that id in both, since the 403 was positional and the
  // config is the better answer.
  const failures = requestedIds.filter((id) => !(id in configs));

  return { configs, failures };
}

/**
 * Merchant-core RTK Query slice: merchant CRUD, per-merchant cancel/reroute/
 * refund/reprint reasons, and the schema-driven configuration envelope.
 * Templates and auth clients live in their own slices (./templates,
 * ./authClient) — see the barrel for the full public surface.
 */
export const merchantApi = createApi({
  reducerPath: 'merchantApi',
  baseQuery: portalBaseQuery,
  tagTypes: [
    'MerchantReason',
    'MerchantConfiguration',
    'MerchantConfigEnvelope',
    'Merchant',
    'MerchantList',
    'MerchantSchema',
    'DataRetentionPolicy',
  ],
  endpoints: (builder) => ({
    /** GET /merchant/v1/merchant/reason?merchantId=&type= */
    getReasons: builder.query<MerchantReason[], { merchantId: string; reasonType: string }>({
      query: ({ merchantId, reasonType }) => ({
        url: `${services.merchant}/v1/merchant/reason?merchantId=${encodeURIComponent(merchantId)}&type=${encodeURIComponent(reasonType)}`,
        method: 'GET',
      }),
      transformResponse: (response: MerchantReason[] | { reasons: MerchantReason[] }) =>
        Array.isArray(response) ? response : (response.reasons ?? []),
      providesTags: (_r, _e, { merchantId, reasonType }) => [
        { type: 'MerchantReason', id: `${merchantId}:${reasonType}` },
      ],
    }),
    /** POST /merchant/v1/merchant/reason */
    createReason: builder.mutation<
      MerchantReason,
      { merchantId: string; reasonType: string; reasonData: ReasonInput }
    >({
      query: ({ merchantId, reasonType, reasonData }) => ({
        url: `${services.merchant}/v1/merchant/reason`,
        method: 'POST',
        body: { ...reasonData, merchantId, type: reasonType },
      }),
      invalidatesTags: (_r, _e, { merchantId, reasonType }) => [
        { type: 'MerchantReason', id: `${merchantId}:${reasonType}` },
      ],
    }),
    /** PUT /merchant/v1/merchant/reason/{reasonId} */
    updateReason: builder.mutation<
      MerchantReason,
      { merchantId: string; reasonType: string; reasonId: string; reasonData: ReasonInput }
    >({
      query: ({ reasonId, reasonData }) => ({
        url: `${services.merchant}/v1/merchant/reason/${encodeURIComponent(reasonId)}`,
        method: 'PUT',
        body: reasonData,
      }),
      invalidatesTags: (_r, _e, { merchantId, reasonType }) => [
        { type: 'MerchantReason', id: `${merchantId}:${reasonType}` },
      ],
    }),
    /** DELETE /merchant/v1/merchant/reason/{reasonId} */
    deleteReason: builder.mutation<
      void,
      { merchantId: string; reasonType: string; reasonId: string }
    >({
      query: ({ reasonId }) => ({
        url: `${services.merchant}/v1/merchant/reason/${encodeURIComponent(reasonId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { merchantId, reasonType }) => [
        { type: 'MerchantReason', id: `${merchantId}:${reasonType}` },
      ],
    }),
    /** GET /merchant/v1/merchant?query=&page=&pageSize=&sortBy=&sortDesc= —
     * lightweight summary list. `sortBy` accepts any Merchant field (e.g.
     * `name`); `sortDesc` flips the direction (server default is ascending).
     * Sort params are only emitted when `sortBy` is set, so callers that don't
     * sort keep the original `?query=&page=&pageSize=` URL. */
    getMerchants: builder.query<
      MerchantSearchResponse,
      { query?: string; page?: number; pageSize?: number; sortBy?: string; sortDesc?: boolean }
    >({
      query: ({ query = '', page = 1, pageSize = 50, sortBy, sortDesc }) => {
        const qs = new URLSearchParams();
        if (query) qs.set('query', query);
        qs.set('page', String(page));
        qs.set('pageSize', String(pageSize));
        if (sortBy) {
          qs.set('sortBy', sortBy);
          qs.set('sortDesc', String(sortDesc ?? false));
        }
        return {
          url: `${services.merchant}/v1/merchant?${qs.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: MerchantSearchResponse | { data?: MerchantSearchResponse }) => {
        const r = response as MerchantSearchResponse & { data?: MerchantSearchResponse };
        if (r.data && Array.isArray(r.data.merchants)) return r.data;
        return r;
      },
      providesTags: [{ type: 'MerchantList', id: 'LIST' }],
    }),
    /**
     * GET /merchant/v1/merchant/search — paginated full merchant rows.
     * Uses the per-id + LIST tag pattern so a single-merchant invalidation
     * doesn't bust every search/page/sort combo cached in the store.
     */
    searchMerchants: builder.query<MerchantListResponse, MerchantSearchArgs>({
      // Optional `merchantIds` scopes results to one org tree:
      //   /merchant/v1/merchant/search?query=&page=1&pageSize=250&sortBy=name&sortAsc=true&merchantIds={id}
      // merchant-service SearchMerchantsHandler expands each id to its org tree
      // (parent → parent + children) and 403s ids outside the caller's access.
      query: ({ query, page = 1, pageSize = 50, sortBy = 'shortId', sortDir = 'desc', merchantIds }) => {
        const qs = new URLSearchParams();
        if (query) qs.set('query', query);
        qs.set('page', String(page));
        qs.set('pageSize', String(pageSize));
        if (sortBy) qs.set('sortBy', sortBy);
        qs.set('sortAsc', String(sortDir === 'asc'));
        for (const id of merchantIds ?? []) qs.append('merchantIds', id);
        return { url: `${services.merchant}/v1/merchant/search?${qs.toString()}`, method: 'GET' };
      },
      transformResponse: (
        response: MerchantListResponse | { data?: MerchantListResponse },
      ) => {
        const r = response as MerchantListResponse & { data?: MerchantListResponse };
        if (r.data && Array.isArray(r.data.merchants)) return r.data;
        return r;
      },
      providesTags: (result) =>
        result?.merchants
          ? [
              ...result.merchants.map((m) => ({ type: 'MerchantList' as const, id: m.id })),
              { type: 'MerchantList' as const, id: 'LIST' },
            ]
          : [{ type: 'MerchantList' as const, id: 'LIST' }],
    }),
    /**
     * GET /merchant/v1/merchant — the Invite-User Step-2 merchant picker,
     * scoped by association type. Direct port of the legacy
     * `merchantServices.getMerchants` used by `InviteUser/Step2MerchantSelection`.
     * Live-confirmed (portal.dev):
     *   parent-merchant → types=Parent (admin, no merchantId)
     *   merchant        → types=Single (admin, no merchantId)
     *   child-merchant  → types=Child & merchantId={parentId}  (returns only that
     *                     parent's subaccounts — e.g. Acmee Inc. → its 3 children)
     * Param order + the `sortDesc = (sortDir==='asc')` quirk + the leading `&`
     * are replicated verbatim from the legacy service so the wire matches.
     * NOTE: the previous Step-2 used the GLOBAL `/merchant/v1/merchant/search`
     * (no types/merchantId), which listed ALL merchants — the subaccount bug.
     */
    getInviteMerchants: builder.query<MerchantListResponse, InviteMerchantsArgs>({
      query: (args) => ({ url: buildInviteMerchantsUrl(args), method: 'GET' }),
      transformResponse: (response: MerchantListResponse | { data?: MerchantListResponse }) => {
        const r = response as MerchantListResponse & { data?: MerchantListResponse };
        if (r.data && Array.isArray(r.data.merchants)) return r.data;
        return r;
      },
      providesTags: [{ type: 'MerchantList', id: 'LIST' }],
    }),
    /** GET /merchant/v1/merchant/{merchantId} — full record. */
    getMerchantById: builder.query<Merchant, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `${services.merchant}/v1/merchant/${encodeURIComponent(merchantId)}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { merchantId }) => [{ type: 'Merchant', id: merchantId }],
    }),
    /** POST /merchant/v1/merchant — create a new merchant. */
    createMerchant: builder.mutation<Merchant, MerchantInput>({
      query: (data) => ({
        url: `${services.merchant}/v1/merchant`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'MerchantList', id: 'LIST' }],
    }),
    /**
     * PUT /merchant/v1/merchant — update an existing merchant.
     * Invalidates: the merchant being updated, the LIST cache, AND both
     * parents (new + previous) when applicable, so parent `children[]`
     * projections refresh after a subaccount is moved. Callers that change
     * the parent must pass `previousParentId` in the arg — `body` strips it
     * before hitting the network.
     */
    updateMerchant: builder.mutation<
      Merchant,
      MerchantInput & { id: string; previousParentId?: string | null }
    >({
      query: ({ previousParentId: _drop, ...data }) => ({
        url: `${services.merchant}/v1/merchant`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_r, _e, arg) => {
        const tags: Array<{ type: 'Merchant' | 'MerchantList'; id: string }> = [
          { type: 'Merchant', id: arg.id },
          { type: 'MerchantList', id: arg.id },
          { type: 'MerchantList', id: 'LIST' },
        ];
        if (arg.parentId) tags.push({ type: 'Merchant', id: arg.parentId });
        if (arg.previousParentId && arg.previousParentId !== arg.parentId) {
          tags.push({ type: 'Merchant', id: arg.previousParentId });
        }
        return tags;
      },
    }),
    /**
     * GET /merchant/v1/merchant/{merchantId}/configuration — lightweight,
     * already used by orders code for refund/reprint feature gates.
     */
    getMerchantConfiguration: builder.query<MerchantConfiguration, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `${services.merchant}/v1/merchant/${merchantId}/configuration`,
        method: 'GET',
      }),
      transformResponse: (response: MerchantConfiguration | { configuration: MerchantConfiguration }) =>
        'configuration' in (response as object)
          ? (response as { configuration: MerchantConfiguration }).configuration
          : (response as MerchantConfiguration),
      providesTags: (_r, _e, { merchantId }) => [
        { type: 'MerchantConfiguration', id: merchantId },
      ],
    }),
    /**
     * GET /config/v1/config?ownerId=&schemaName=MerchantsConfiguration —
     * the schema-driven config envelope used by SettingsView. Different from
     * `getMerchantConfiguration` (which goes through the merchant service).
     */
    getMerchantConfigEnvelope: builder.query<MerchantConfigEnvelope, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `${services.config}/v1/config?ownerId=${encodeURIComponent(merchantId)}&schemaName=MerchantsConfiguration`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { merchantId }) => [
        { type: 'MerchantConfigEnvelope', id: merchantId },
      ],
    }),
    /** PUT /config/v1/config — apply a settings update. */
    updateMerchantConfigEnvelope: builder.mutation<MerchantConfigEnvelope, MerchantConfigEnvelope>({
      query: (data) => ({
        url: `${services.config}/v1/config`,
        method: 'PUT',
        body: data,
      }),
      // A config save can change the resolved retention window of this account
      // AND of every sub-account inheriting from it, so the whole policy cache
      // is dropped rather than just the owner's entry.
      invalidatesTags: (_r, _e, arg) => [
        { type: 'MerchantConfigEnvelope', id: arg.ownerId },
        { type: 'DataRetentionPolicy' },
      ],
    }),
    /**
     * LIVE: GET /merchant/v1/merchant/{merchantId}/dataRetentionPolicy — the
     * effective PII-redaction window: the account's own configured value, else
     * the parent's, else unset. Admin-only on the API; the portal shows it to
     * super-admins next to the DataRetention setting.
     */
    getDataRetentionPolicy: builder.query<DataRetentionPolicy, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `${services.merchant}/v1/merchant/${encodeURIComponent(merchantId)}/dataRetentionPolicy`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { merchantId }) => [
        { type: 'DataRetentionPolicy', id: merchantId },
      ],
    }),
    /**
     * GET /config/v1/config/bulk?bulk=&bulk=&schemaName=MerchantsConfiguration — resolve the
     * config for a page of merchants in one request, for list views that would otherwise fire
     * one request per row.
     *
     * Rows come back in request order and carry no owner id of their own on the error path, so
     * they are zipped against the ids that were asked for. A non-200 row is reported as a
     * failure rather than folded in as an empty config: for a list of settings, "could not read"
     * and "set to false" must not collapse into the same rendering.
     */
    getMerchantConfigsBulk: builder.query<BulkConfigResult, { merchantIds: string[] }>({
      query: ({ merchantIds }) => ({
        url: `${services.config}/v1/config/bulk?${merchantIds
          .map((id) => `bulk=${encodeURIComponent(id)}`)
          .join('&')}&schemaName=MerchantsConfiguration`,
        method: 'GET',
      }),
      transformResponse: (
        response: { bulk?: Array<{ status?: number; response?: MerchantConfigEnvelope }> },
        _meta,
        arg,
      ) => unwrapBulkConfigs(response, arg.merchantIds),
      providesTags: (_r, _e, { merchantIds }) =>
        merchantIds.map((id) => ({ type: 'MerchantConfigEnvelope' as const, id })),
    }),

    /** GET /config/v1/schema/MerchantsConfiguration — settings schema. */
    getMerchantSchema: builder.query<MerchantSchema, void>({
      query: () => ({
        url: `${services.config}/v1/schema/MerchantsConfiguration`,
        method: 'GET',
      }),
      providesTags: ['MerchantSchema'],
    }),
    /**
     * GET /merchant/v1/merchant/bulk?bulk={id}&bulk={id}… — resolve several
     * merchants by id. Used by the catalog list, the user org chips and picker,
     * and the admin-view account columns.
     *
     * The service accepts at most `MERCHANT_BULK_MAX_IDS` ids per request and
     * rejects a bigger payload outright, so the ids are split into batches of
     * that size, fetched with bounded concurrency and merged in request order.
     * A parent user with 50+ subaccounts, or a 100-row list page, would
     * otherwise fail as a whole. Any batch failing fails the query: a silently
     * incomplete org list must not look like a complete one. An empty id list
     * resolves to [] without a request.
     */
    getMerchantsBulk: builder.query<Merchant[], { merchantIds: string[] }>({
      async queryFn({ merchantIds }, _api, _extra, fetchWithBQ) {
        const batches: string[][] = [];
        for (let i = 0; i < merchantIds.length; i += MERCHANT_BULK_MAX_IDS) {
          batches.push(merchantIds.slice(i, i + MERCHANT_BULK_MAX_IDS));
        }
        const results = await runInChunks(
          batches,
          async (batch) =>
            fetchWithBQ({
              url: `${services.merchant}/v1/merchant/bulk?${batch
                .map((id) => `bulk=${encodeURIComponent(id)}`)
                .join('&')}`,
              method: 'GET',
            }),
          { chunk: MERCHANT_BULK_CONCURRENCY },
        );
        const failed = results.find((r) => r.error);
        if (failed?.error) return { error: failed.error };
        return { data: results.flatMap((r) => unwrapMerchantsBulk(r.data as MerchantsBulkEnvelope)) };
      },
      providesTags: (result) =>
        (result ?? []).map((m) => ({ type: 'Merchant' as const, id: m.id })),
    }),
  }),
});

export const {
  useGetReasonsQuery,
  useCreateReasonMutation,
  useUpdateReasonMutation,
  useDeleteReasonMutation,
  useGetMerchantsQuery,
  useSearchMerchantsQuery,
  useGetInviteMerchantsQuery,
  useGetMerchantByIdQuery,
  useLazyGetMerchantByIdQuery,
  useCreateMerchantMutation,
  useUpdateMerchantMutation,
  useGetMerchantConfigurationQuery,
  useGetMerchantConfigEnvelopeQuery,
  useGetMerchantConfigsBulkQuery,
  useGetDataRetentionPolicyQuery,
  useUpdateMerchantConfigEnvelopeMutation,
  useGetMerchantSchemaQuery,
  useGetMerchantsBulkQuery,
  useLazyGetMerchantsBulkQuery,
} = merchantApi;
